import api from '../api';

const SYNC_KEYS = ['learnedCourses', 'collectedVideos', 'learningStats', 'unknownWords'];
const VOCAB_PREFIX = 'vocabularyStatus_';

let syncTimer = null;
let isSyncing = false;
let pendingSync = false;

const collectLearningData = () => {
  const data = {
    learnedCourses: [],
    collectedVideos: [],
    learningStats: {},
    unknownWords: []
  };

  try {
    const learned = localStorage.getItem('learnedCourses');
    if (learned) data.learnedCourses = JSON.parse(learned.replace(/^"undefined"$/, '[]'));
  } catch (e) {}

  try {
    const collected = localStorage.getItem('collectedVideos');
    if (collected) data.collectedVideos = JSON.parse(collected.replace(/^"undefined"$/, '[]'));
  } catch (e) {}

  try {
    const stats = localStorage.getItem('learningStats');
    if (stats) data.learningStats = JSON.parse(stats);
  } catch (e) {}

  try {
    const words = localStorage.getItem('unknownWords');
    if (words) data.unknownWords = JSON.parse(words.replace(/^"undefined"$/, '[]'));
  } catch (e) {}

  const vocabularyStatus = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(VOCAB_PREFIX)) {
      const videoId = key.substring(VOCAB_PREFIX.length);
      try {
        vocabularyStatus[videoId] = JSON.parse(localStorage.getItem(key));
      } catch (e) {}
    }
  }
  data.vocabularyStatus = vocabularyStatus;

  return data;
};

const isDataEmpty = (data) => {
  if (!data) return true;
  const learnedEmpty = !data.learnedCourses || data.learnedCourses.length === 0;
  const collectedEmpty = !data.collectedVideos || data.collectedVideos.length === 0;
  const statsEmpty = !data.learningStats || Object.keys(data.learningStats).length === 0;
  const wordsEmpty = !data.unknownWords || data.unknownWords.length === 0;
  const vocabEmpty = !data.vocabularyStatus || Object.keys(data.vocabularyStatus).length === 0;
  return learnedEmpty && collectedEmpty && statsEmpty && wordsEmpty && vocabEmpty;
};

const mergeLearningData = (remoteData, localData) => {
  const merged = { ...remoteData };

  if (localData.learnedCourses && localData.learnedCourses.length > 0) {
    const remoteSet = new Set((remoteData.learnedCourses || []).map(x => typeof x === 'string' ? x : x.id));
    const localItems = localData.learnedCourses.filter(x => {
      const id = typeof x === 'string' ? x : x.id;
      return !remoteSet.has(id);
    });
    merged.learnedCourses = [...(remoteData.learnedCourses || []), ...localItems];
  }

  if (localData.collectedVideos && localData.collectedVideos.length > 0) {
    const remoteSet = new Set((remoteData.collectedVideos || []).map(x => typeof x === 'string' ? x : x.id));
    const localItems = localData.collectedVideos.filter(x => {
      const id = typeof x === 'string' ? x : x.id;
      return !remoteSet.has(id);
    });
    merged.collectedVideos = [...(remoteData.collectedVideos || []), ...localItems];
  }

  if (localData.learningStats && Object.keys(localData.learningStats).length > 0) {
    const remoteStats = remoteData.learningStats || {};
    const localStats = localData.learningStats;
    const mergedStats = { ...localStats, ...remoteStats };

    if (Array.isArray(localStats.learningDays) || Array.isArray(remoteStats.learningDays)) {
      const daysSet = new Set([...(localStats.learningDays || []), ...(remoteStats.learningDays || [])]);
      mergedStats.learningDays = Array.from(daysSet).sort((a, b) => new Date(a) - new Date(b));
    }

    if (Array.isArray(localStats.completedVideos) || Array.isArray(remoteStats.completedVideos)) {
      const videosSet = new Set([...(localStats.completedVideos || []), ...(remoteStats.completedVideos || [])]);
      mergedStats.completedVideos = Array.from(videosSet);
    }

    if (typeof localStats.totalMinutes === 'number' || typeof remoteStats.totalMinutes === 'number') {
      mergedStats.totalMinutes = Math.max(localStats.totalMinutes || 0, remoteStats.totalMinutes || 0);
    }

    if (typeof localStats.consecutiveDays === 'number' || typeof remoteStats.consecutiveDays === 'number') {
      mergedStats.consecutiveDays = Math.max(localStats.consecutiveDays || 0, remoteStats.consecutiveDays || 0);
    }

    merged.learningStats = mergedStats;
  }

  if (localData.unknownWords && localData.unknownWords.length > 0) {
    const remoteSet = new Set(remoteData.unknownWords || []);
    const localItems = localData.unknownWords.filter(w => !remoteSet.has(w));
    merged.unknownWords = [...(remoteData.unknownWords || []), ...localItems];
  }

  if (localData.vocabularyStatus && Object.keys(localData.vocabularyStatus).length > 0) {
    merged.vocabularyStatus = { ...localData.vocabularyStatus, ...(remoteData.vocabularyStatus || {}) };
  }

  return merged;
};

const applyDataToLocal = (data) => {
  const safeSetItem = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.warn(`localStorage 配额超限，跳过 ${key} 的本地存储`);
      } else {
        throw e;
      }
    }
  };

  if (data.learnedCourses) {
    safeSetItem('learnedCourses', JSON.stringify(data.learnedCourses));
  }
  if (data.collectedVideos) {
    safeSetItem('collectedVideos', JSON.stringify(data.collectedVideos));
  }
  if (data.learningStats) {
    safeSetItem('learningStats', JSON.stringify(data.learningStats));
  }
  if (data.unknownWords) {
    safeSetItem('unknownWords', JSON.stringify(data.unknownWords));
  }
  if (data.vocabularyStatus) {
    Object.entries(data.vocabularyStatus).forEach(([videoId, status]) => {
      safeSetItem(`${VOCAB_PREFIX}${videoId}`, JSON.stringify(status));
    });
  }

  // 触发自定义事件，通知组件数据已更新
  window.dispatchEvent(new CustomEvent('learningDataUpdated', {
    detail: {
      learnedCourses: data.learnedCourses,
      collectedVideos: data.collectedVideos,
      learningStats: data.learningStats,
      unknownWords: data.unknownWords,
      vocabularyStatus: data.vocabularyStatus
    }
  }));
};

const doSync = async () => {
  const token = localStorage.getItem('token');
  if (!token || token.startsWith('mock-token')) return;

  if (isSyncing) {
    pendingSync = true;
    return;
  }

  isSyncing = true;
  try {
    const localData = collectLearningData();

    let remoteData = null;
    try {
      const res = await api.get('/learning');
      remoteData = res.data;
    } catch (e) {
      console.warn('获取远程数据失败，直接上传本地数据:', e);
    }

    if (remoteData && !isDataEmpty(remoteData)) {
      const merged = mergeLearningData(remoteData, localData);
      applyDataToLocal(merged);
      await api.put('/learning', merged);
    } else {
      await api.put('/learning', localData);
    }
  } catch (e) {
    console.error('学习数据同步失败:', e);
  } finally {
    isSyncing = false;
    if (pendingSync) {
      pendingSync = false;
      scheduleSync();
    }
  }
};

export const scheduleSync = () => {
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(() => {
    doSync().catch(() => {});
  }, 3000);
};

export const syncLearningData = doSync;

export const syncOnLogin = (learningData) => {
  if (!learningData) {
    scheduleSync();
    return;
  }

  try {
    const localData = collectLearningData();
    const remoteEmpty = isDataEmpty(learningData);
    const localEmpty = isDataEmpty(localData);

    if (remoteEmpty && !localEmpty) {
      scheduleSync();
      return;
    }

    if (!remoteEmpty && !localEmpty) {
      const merged = mergeLearningData(learningData, localData);
      applyDataToLocal(merged);
      scheduleSync();
      return;
    }

    applyDataToLocal(learningData);
  } catch (e) {
    console.error('登录同步数据失败:', e);
  }

  scheduleSync();
};

export const fetchAndApplyLearningData = async () => {
  const token = localStorage.getItem('token');
  if (!token || token.startsWith('mock-token')) return;

  try {
    const res = await api.get('/learning');
    const remoteData = res.data;
    const localData = collectLearningData();

    const remoteEmpty = isDataEmpty(remoteData);
    const localEmpty = isDataEmpty(localData);

    if (remoteEmpty && !localEmpty) {
      await doSync();
      return;
    }

    if (!remoteEmpty && !localEmpty) {
      const merged = mergeLearningData(remoteData, localData);
      applyDataToLocal(merged);
      await api.put('/learning', merged);
      return;
    }

    if (!remoteEmpty && localEmpty) {
      applyDataToLocal(remoteData);
    }
  } catch (e) {
    console.error('拉取学习数据失败:', e);
  }
};

let interceptorInstalled = false;

export const installStorageSyncInterceptor = () => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  const originalSetItem = localStorage.setItem.bind(localStorage);

  localStorage.setItem = function(key, value) {
    try {
      originalSetItem(key, value);
      if (SYNC_KEYS.includes(key) || key.startsWith(VOCAB_PREFIX)) {
        scheduleSync();
      }
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.warn(`localStorage 配额超限，跳过 ${key} 的存储`);
      } else {
        throw e;
      }
    }
  };
};
